import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown, ChevronRight, LayoutList, Layers, Plus, Pencil, Search, Trash2, ExternalLink, Paperclip, X } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FileUpload } from '@/components/ui/FileUpload';
import { Card, CardContent } from '@/components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { createLancamentoInput, type CreateLancamentoInput } from '@portal-leo/shared';

type Lancamento = {
  id: string;
  numero: number;
  data: string;
  contaGabaritoId: string;
  contaNome: string;
  contaCodigo: number;
  natureza: 'RECEITA' | 'DESPESA';
  descricao: string;
  valor: string;
  linkNotaFiscal: string | null;
  linkAnexo: string | null;
  origem: string;
  origemId: string | null;
};

interface LivroCaixaTableProps {
  eventoId: string;
}

export function LivroCaixaTable({ eventoId }: LivroCaixaTableProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'FINANCEIRO', 'COORDENADOR_EVENTO');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<Lancamento | null>(null);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'list' | 'grouped'>('list');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [filtroNatureza, setFiltroNatureza] = useState<'' | 'RECEITA' | 'DESPESA'>('');
  const [filtroConta, setFiltroConta] = useState('');
  const [filtroOrigem, setFiltroOrigem] = useState<'' | 'MANUAL' | 'INSCRICAO' | 'PATROCINIO' | 'GASTO'>('');
  const utils = trpc.useUtils();

  const isGrouped = viewMode === 'grouped';

  const { data, isLoading } = trpc.livroCaixa.listLancamentos.useQuery({
    eventoId,
    page: isGrouped ? 1 : page,
    limit: isGrouped ? 500 : 20,
    search: search || undefined,
    natureza: filtroNatureza || undefined,
    contaGabaritoId: filtroConta || undefined,
    origem: filtroOrigem || undefined,
  }, { placeholderData: (prev) => prev });
  const lancamentos = data?.items;
  const { data: gabaritoData } = trpc.livroCaixa.listGabarito.useQuery({ eventoId, page: 1, limit: 500 });
  const gabarito = gabaritoData?.items;
  const { data: resumo } = trpc.livroCaixa.getResumoFinanceiro.useQuery({ eventoId });

  const resetPage = () => setPage(1);
  const filtrosAtivos = search || filtroNatureza || filtroConta || filtroOrigem;

  const grupos = (() => {
    if (!isGrouped || !lancamentos) return [] as Array<{ key: string; contaNome: string; contaCodigo: number; natureza: 'RECEITA' | 'DESPESA'; items: Lancamento[]; valorTotal: number }>;
    const map = new Map<string, { key: string; contaNome: string; contaCodigo: number; natureza: 'RECEITA' | 'DESPESA'; items: Lancamento[]; valorTotal: number }>();
    for (const l of lancamentos) {
      const key = l.contaGabaritoId;
      const g = map.get(key) ?? {
        key,
        contaNome: l.contaNome,
        contaCodigo: l.contaCodigo,
        natureza: l.natureza,
        items: [],
        valorTotal: 0,
      };
      g.items.push(l);
      g.valorTotal += Number(l.valor || 0);
      map.set(key, g);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.natureza !== b.natureza) return a.natureza === 'RECEITA' ? -1 : 1;
      return a.contaCodigo - b.contaCodigo;
    });
  })();

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const invalidateAll = () => {
    utils.livroCaixa.listLancamentos.invalidate();
    utils.livroCaixa.getResumoFinanceiro.invalidate({ eventoId });
    utils.livroCaixa.getBalancete.invalidate({ eventoId });
    utils.livroCaixa.getSaldosDiarios.invalidate({ eventoId });
    utils.eventos.listPatrocinadores.invalidate({ id: eventoId });
    utils.eventos.getById.invalidate({ id: eventoId });
    utils.eventos.getFinanceiro.invalidate({ id: eventoId });
  };

  const addMutation = trpc.livroCaixa.addLancamento.useMutation({
    onSuccess: () => {
      invalidateAll();
      handleCloseModal();
    },
  });

  const updateMutation = trpc.livroCaixa.updateLancamento.useMutation({
    onSuccess: () => {
      invalidateAll();
      handleCloseModal();
    },
    onError: (err) => {
      alert(err.message);
    },
  });

  const removeMutation = trpc.livroCaixa.removeLancamento.useMutation({
    onSuccess: () => {
      utils.livroCaixa.listLancamentos.invalidate();
      utils.livroCaixa.getResumoFinanceiro.invalidate({ eventoId });
      utils.livroCaixa.getBalancete.invalidate({ eventoId });
      utils.livroCaixa.getSaldosDiarios.invalidate({ eventoId });
      utils.eventos.listPatrocinadores.invalidate();
      utils.eventos.getById.invalidate({ id: eventoId });
      utils.eventos.getFinanceiro.invalidate({ id: eventoId });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateLancamentoInput>({
    resolver: zodResolver(createLancamentoInput),
    defaultValues: { eventoId },
  });

  const linkAnexoValue = watch('linkAnexo');
  const linkNotaFiscalValue = watch('linkNotaFiscal');
  const contaGabaritoId = watch('contaGabaritoId');
  const isPatrocinio = gabarito?.some(
    (g) => g.id === contaGabaritoId &&
      (g.nome.toLowerCase().includes('patrocínio') || g.nome.toLowerCase().includes('patrocinio'))
  );

  const onSubmit = (data: CreateLancamentoInput) => {
    if (editing) {
      updateMutation.mutate({
        id: editing.id,
        data: data.data,
        contaGabaritoId: data.contaGabaritoId,
        descricao: data.descricao,
        valor: data.valor,
        linkNotaFiscal: data.linkNotaFiscal || null,
        linkAnexo: data.linkAnexo || null,
      });
    } else {
      addMutation.mutate(data);
    }
  };

  const handleOpenModal = () => {
    setEditing(null);
    reset({
      eventoId,
      data: new Date().toISOString().split('T')[0],
      contaGabaritoId: '',
      descricao: '',
      valor: '',
      linkNotaFiscal: '',
      linkAnexo: '',
      patrocinadorNome: '',
      patrocinadorCnpjCpf: '',
      patrocinadorTipo: undefined,
      patrocinadorStatus: undefined,
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (l: Lancamento) => {
    setEditing(l);
    reset({
      eventoId,
      data: l.data,
      contaGabaritoId: l.contaGabaritoId,
      descricao: l.descricao,
      valor: l.valor,
      linkNotaFiscal: l.linkNotaFiscal || '',
      linkAnexo: l.linkAnexo || '',
      patrocinadorNome: '',
      patrocinadorCnpjCpf: '',
      patrocinadorTipo: undefined,
      patrocinadorStatus: undefined,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditing(null);
    reset();
  };

  // Editável: lançamentos manuais (qualquer natureza) e todas as receitas,
  // inclusive as vindas de inscrição/patrocínio (valor propaga para a origem).
  const canEditLancamento = (l: Lancamento) => l.origem === 'MANUAL' || l.natureza === 'RECEITA';

  // Ao editar um lançamento vinculado (não manual), a natureza é fixa.
  const isVinculadoEdit = editing != null && editing.origem !== 'MANUAL';

  const handleRemove = (id: string) => {
    if (window.confirm('Tem certeza que deseja remover este lançamento?')) {
      removeMutation.mutate({ id });
    }
  };

  const formatCurrency = (value: string | number | null) => {
    if (!value) return 'R$ 0,00';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
  };

  const formatDate = (date: string) => {
    const [y, m, d] = date.split('-');
    return `${d}/${m}/${y}`;
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Receitas</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(resumo?.totalReceitas || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Despesas</p>
            <p className="text-xl font-bold text-red-600">{formatCurrency(resumo?.totalDespesas || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Saldo</p>
            <p className={`text-xl font-bold ${(resumo?.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(resumo?.saldo || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{data?.meta.total || 0} lançamentos</p>
        <div className="flex items-center gap-3">
          <div className="inline-flex overflow-hidden rounded-lg border border-gray-200 bg-white text-sm">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1 px-3 py-1.5 ${viewMode === 'list' ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'}`}
              aria-pressed={viewMode === 'list'}
            >
              <LayoutList className="h-4 w-4" />
              Lista
            </button>
            <button
              onClick={() => setViewMode('grouped')}
              className={`flex items-center gap-1 border-l border-gray-200 px-3 py-1.5 ${viewMode === 'grouped' ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'}`}
              aria-pressed={viewMode === 'grouped'}
            >
              <Layers className="h-4 w-4" />
              Agrupada por conta
            </button>
          </div>
          <Button onClick={handleOpenModal} disabled={!canEdit || !gabarito || gabarito.length === 0}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Lançamento
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por descrição ou valor..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
            className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-gray-300 bg-white text-sm">
          {(['', 'RECEITA', 'DESPESA'] as const).map((n, idx) => (
            <button
              key={n || 'todas'}
              onClick={() => { setFiltroNatureza(n); resetPage(); }}
              className={`${idx > 0 ? 'border-l border-gray-300' : ''} px-3 py-1.5 ${
                filtroNatureza === n
                  ? n === 'RECEITA' ? 'bg-green-50 text-green-700' : n === 'DESPESA' ? 'bg-red-50 text-red-700' : 'bg-primary-50 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {n === '' ? 'Todas' : n === 'RECEITA' ? 'Receitas' : 'Despesas'}
            </button>
          ))}
        </div>
        <select
          value={filtroConta}
          onChange={(e) => { setFiltroConta(e.target.value); resetPage(); }}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Todas as contas</option>
          {gabarito?.map((c) => (
            <option key={c.id} value={c.id}>{c.codigo} - {c.nome}</option>
          ))}
        </select>
        <select
          value={filtroOrigem}
          onChange={(e) => { setFiltroOrigem(e.target.value as typeof filtroOrigem); resetPage(); }}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Todas as origens</option>
          <option value="MANUAL">Manual</option>
          <option value="INSCRICAO">Inscrição</option>
          <option value="PATROCINIO">Patrocínio</option>
          <option value="GASTO">Gasto</option>
        </select>
        {filtrosAtivos && (
          <button
            onClick={() => { setSearch(''); setFiltroNatureza(''); setFiltroConta(''); setFiltroOrigem(''); resetPage(); }}
            className="flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
            Limpar
          </button>
        )}
      </div>

      {!gabarito || gabarito.length === 0 ? (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-center">
          <p className="text-sm text-yellow-800">
            Configure o Gabarito de Contas antes de criar lançamentos.
          </p>
        </div>
      ) : null}

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {!lancamentos || lancamentos.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <p>Nenhum lançamento registrado</p>
            </div>
          ) : isGrouped ? (
            <div className="divide-y divide-gray-100">
              {grupos.map((g) => {
                const expanded = expandedGroups.has(g.key);
                const isReceita = g.natureza === 'RECEITA';
                return (
                  <div key={g.key}>
                    <button
                      onClick={() => toggleGroup(g.key)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        {expanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                        <span className="text-xs text-gray-500">{g.contaCodigo}</span>
                        <span className="font-medium text-gray-900">{g.contaNome}</span>
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          isReceita ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {isReceita ? 'Receita' : 'Despesa'}
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                          {g.items.length}
                        </span>
                      </div>
                      <span className={`text-sm font-semibold ${isReceita ? 'text-green-600' : 'text-red-600'}`}>
                        {!isReceita && '-'}{formatCurrency(g.valorTotal)}
                      </span>
                    </button>
                    {expanded && (
                      <div className="bg-gray-50 px-2 pb-2">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12">Nº</TableHead>
                              <TableHead>Data</TableHead>
                              <TableHead>Descrição</TableHead>
                              <TableHead className="text-right">Valor</TableHead>
                              <TableHead className="w-16">Links</TableHead>
                              <TableHead className="text-right w-16">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {g.items.map((l) => (
                              <TableRow key={l.id}>
                                <TableCell className="text-gray-500">{l.numero}</TableCell>
                                <TableCell>{formatDate(l.data)}</TableCell>
                                <TableCell>
                                  {l.descricao}
                                  {l.origem === 'PATROCINIO' && (
                                    <span className="ml-1 inline-flex rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs text-indigo-700">Patrocínio</span>
                                  )}
                                  {l.origem === 'INSCRICAO' && (
                                    <span className="ml-1 inline-flex rounded-full bg-cyan-100 px-1.5 py-0.5 text-xs text-cyan-700">Inscrição</span>
                                  )}
                                  {l.origem === 'GASTO' && (
                                    <span className="ml-1 inline-flex rounded-full bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">Gasto</span>
                                  )}
                                </TableCell>
                                <TableCell className={`text-right font-medium ${isReceita ? 'text-green-600' : 'text-red-600'}`}>
                                  {!isReceita && '-'}{formatCurrency(l.valor)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    {l.linkAnexo && (
                                      <a href={l.linkAnexo} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600" title="Comprovante">
                                        <Paperclip className="h-4 w-4" />
                                      </a>
                                    )}
                                    {l.linkNotaFiscal && (
                                      <a href={l.linkNotaFiscal} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600" title="Nota Fiscal">
                                        <ExternalLink className="h-4 w-4" />
                                      </a>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    {canEditLancamento(l) && (
                                      <Button variant="ghost" size="sm" onClick={() => handleOpenEditModal(l)} disabled={!canEdit} title="Editar">
                                        <Pencil className="h-4 w-4 text-gray-500" />
                                      </Button>
                                    )}
                                    {(l.origem === 'MANUAL' || l.origem === 'PATROCINIO') && (
                                      <Button variant="ghost" size="sm" onClick={() => handleRemove(l.id)} disabled={!canEdit || removeMutation.isPending} title="Remover">
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-end gap-6 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">
                <span className="text-green-600">Receitas: {formatCurrency(resumo?.totalReceitas || 0)}</span>
                <span className="text-red-600">Despesas: -{formatCurrency(resumo?.totalDespesas || 0)}</span>
                <span className={(resumo?.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                  Saldo: {formatCurrency(resumo?.saldo || 0)}
                </span>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Nº</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Conta</TableHead>
                  <TableHead className="w-12">Cód</TableHead>
                  <TableHead>Natureza</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="w-16">Links</TableHead>
                  <TableHead className="text-right w-16">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lancamentos.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-gray-500">{l.numero}</TableCell>
                    <TableCell>{formatDate(l.data)}</TableCell>
                    <TableCell className="font-medium">{l.contaNome}</TableCell>
                    <TableCell className="text-gray-500">{l.contaCodigo}</TableCell>
                    <TableCell>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        l.natureza === 'RECEITA'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {l.natureza === 'RECEITA' ? 'Receita' : 'Despesa'}
                      </span>
                      {l.origem === 'PATROCINIO' && (
                        <span className="ml-1 inline-flex rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs text-indigo-700">
                          Patrocínio
                        </span>
                      )}
                      {l.origem === 'INSCRICAO' && (
                        <span className="ml-1 inline-flex rounded-full bg-cyan-100 px-1.5 py-0.5 text-xs text-cyan-700">
                          Inscrição
                        </span>
                      )}
                      {l.origem === 'GASTO' && (
                        <span className="ml-1 inline-flex rounded-full bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                          Gasto
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{l.descricao}</TableCell>
                    <TableCell className={`text-right font-medium ${
                      l.natureza === 'RECEITA' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {l.natureza === 'DESPESA' && '-'}{formatCurrency(l.valor)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {l.linkAnexo && (
                          <a href={l.linkAnexo} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600" title="Comprovante">
                            <Paperclip className="h-4 w-4" />
                          </a>
                        )}
                        {l.linkNotaFiscal && (
                          <a href={l.linkNotaFiscal} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600" title="Nota Fiscal">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canEditLancamento(l) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEditModal(l)}
                            disabled={!canEdit}
                            className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4 text-gray-500" />
                          </Button>
                        )}
                        {(l.origem === 'MANUAL' || l.origem === 'PATROCINIO') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(l.id)}
                            disabled={!canEdit || removeMutation.isPending}
                            className={!canEdit ? 'opacity-50 cursor-not-allowed' : ''}
                            title="Remover"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals row */}
                <TableRow className="bg-gray-50 font-semibold">
                  <TableCell colSpan={6} className="text-right">Totais:</TableCell>
                  <TableCell className="text-right">
                    <div className="text-green-600">{formatCurrency(resumo?.totalReceitas || 0)}</div>
                    <div className="text-red-600">-{formatCurrency(resumo?.totalDespesas || 0)}</div>
                    <div className={`border-t border-gray-300 pt-1 ${(resumo?.saldo || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(resumo?.saldo || 0)}
                    </div>
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          )}
          {data && !isGrouped && (
            <div className="px-4 pb-2">
              <Pagination meta={data.meta} onPageChange={setPage} itemLabel="lançamentos" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editing ? 'Editar Lançamento' : 'Novo Lançamento'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" {...register('eventoId')} />

          <div className="grid grid-cols-2 gap-4">
            <Input label="Data" type="date" {...register('data')} error={errors.data?.message} />
            <Input label="Valor" type="number" step="0.01" {...register('valor')} error={errors.valor?.message} />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Conta</label>
            <select
              {...register('contaGabaritoId')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Selecione uma conta</option>
              {(!isVinculadoEdit || editing?.natureza === 'RECEITA') && (
                <optgroup label="Receitas">
                  {gabarito?.filter((g) => g.natureza === 'RECEITA' && g.childCount === 0).map((g) => (
                    <option key={g.id} value={g.id}>{g.codigo} - {g.nome}</option>
                  ))}
                </optgroup>
              )}
              {(!isVinculadoEdit || editing?.natureza === 'DESPESA') && (
                <optgroup label="Despesas">
                  {gabarito?.filter((g) => g.natureza === 'DESPESA' && g.childCount === 0).map((g) => (
                    <option key={g.id} value={g.id}>{g.codigo} - {g.nome}</option>
                  ))}
                </optgroup>
              )}
            </select>
            {errors.contaGabaritoId && (
              <p className="mt-1 text-sm text-red-600">{errors.contaGabaritoId.message}</p>
            )}
            {isVinculadoEdit && (
              <p className="mt-1 text-xs text-gray-500">
                Receita vinculada à origem ({editing?.origem === 'PATROCINIO' ? 'patrocínio' : 'inscrição'}).
                Alterar o valor também atualiza o registro de origem e a conta a receber.
              </p>
            )}
          </div>

          <Input label="Descrição" {...register('descricao')} error={errors.descricao?.message} />

          {isPatrocinio && !editing && (
            <div className="space-y-4 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
              <p className="text-sm font-medium text-indigo-700">Dados do Patrocinador</p>
              <Input
                label="Nome do Patrocinador"
                {...register('patrocinadorNome')}
                error={errors.patrocinadorNome?.message}
              />
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="CNPJ/CPF"
                  {...register('patrocinadorCnpjCpf')}
                />
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
                  <select
                    {...register('patrocinadorTipo')}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    <option value="APOIO">Apoio</option>
                    <option value="BRONZE">Bronze</option>
                    <option value="PRATA">Prata</option>
                    <option value="OURO">Ouro</option>
                    <option value="DIAMANTE">Diamante</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                <select
                  {...register('patrocinadorStatus')}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="PENDENTE">Pendente</option>
                  <option value="RECEBIDO">Recebido</option>
                  <option value="PERMUTA">Permuta</option>
                </select>
              </div>
              {watch('patrocinadorStatus') === 'PERMUTA' && (
                <p className="text-xs text-indigo-600">
                  * Permuta não será contabilizada no livro caixa
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FileUpload
              label="Nota Fiscal"
              value={linkNotaFiscalValue || undefined}
              onUploadComplete={(url) => setValue('linkNotaFiscal', url)}
              onClear={() => setValue('linkNotaFiscal', '')}
            />
            <FileUpload
              label="Comprovante"
              value={linkAnexoValue || undefined}
              onUploadComplete={(url) => setValue('linkAnexo', url)}
              onClear={() => setValue('linkAnexo', '')}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal}>Cancelar</Button>
            <Button type="submit" isLoading={addMutation.isPending || updateMutation.isPending}>
              {editing ? 'Salvar' : 'Adicionar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
